import axios from 'axios'
import { functions } from 'fb/index'
import { httpsCallable } from 'firebase/functions'
import { OcrApiResponse } from 'types/ocr'
import { apiClient } from 'api/client'
import { isRestOcrEnabled } from 'api/featureFlags'

// ファイルをBase64に変換するヘルパー関数
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // data:image/jpeg;base64, の部分を除去
      const base64 = result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export const analyzeImage = async (files: File[]): Promise<OcrApiResponse> => {
  try {
    console.log('[OCR API] リクエスト開始', {
      fileCount: files.length,
      files: files.map((f) => ({
        name: f.name,
        size: f.size,
        type: f.type,
      })),
    })

    // ファイルをBase64に変換
    const filesData = await Promise.all(
      files.map(async (file) => ({
        name: file.name,
        data: await fileToBase64(file),
        type: file.type,
      })),
    )

    // REST 切替フラグ ON: daseruno-api を直接叩く
    if (isRestOcrEnabled()) {
      console.log('[OCR API] daseruno-api 呼び出し中...')
      try {
        const res = await apiClient.post(
          '/v1/ingredients/ocr/analyze',
          { files: filesData },
          { timeout: 300000 }, // 5分
        )
        const response = res.data as {
          status: string
          data: OcrApiResponse
        }
        if (response.status !== 'success') {
          throw new Error('OCR APIでエラーが発生しました')
        }
        console.log('[OCR API] レスポンス成功', response.data)
        return response.data
      } catch (error: any) {
        console.error('[OCR API] エラー詳細:', {
          error,
          message: error?.message,
          code: error?.code,
          status: error?.response?.status,
        })
        if (axios.isAxiosError(error)) {
          const status = error.response?.status
          if (status === 401) {
            console.error('[OCR API] 認証エラー')
            throw new Error('認証が必要です。ログインしてください。')
          }
          if (status === 504) {
            console.error('[OCR API] タイムアウト')
            throw new Error(
              '処理がタイムアウトしました。もう一度お試しください。',
            )
          }
          const data = error.response?.data as
            | { message?: string | string[] }
            | undefined
          const raw = data?.message
          const message = Array.isArray(raw) ? raw.join(', ') : raw
          if (message) {
            throw new Error(message)
          }
        }
        // axios 以外 / 上で投げた Error はそのまま再 throw
        if (error instanceof Error) {
          throw error
        }
        console.error('[OCR API] 不明なエラー:', error)
        throw new Error('画像の解析に失敗しました。もう一度お試しください。')
      }
    }

    console.log('[OCR API] Cloud Function呼び出し中...')

    // Cloud Functionを呼び出し（タイムアウト5分に設定）
    const analyzeImageFunction = httpsCallable(
      functions,
      'apiAnalyzeImageWithOcr',
      {
        timeout: 300000, // 5分（Cloud Functionsのタイムアウトと合わせる）
      },
    )
    const result = await analyzeImageFunction({ files: filesData })

    const response = result.data as {
      status: string
      data: OcrApiResponse
    }

    if (response.status !== 'success') {
      throw new Error('OCR APIでエラーが発生しました')
    }

    console.log('[OCR API] レスポンス成功', response.data)
    return response.data
  } catch (error: any) {
    console.error('[OCR API] エラー詳細:', {
      error,
      message: error.message,
      code: error.code,
      details: error.details,
    })

    // Firebase Functionsのエラーハンドリング
    if (error.code === 'unauthenticated') {
      console.error('[OCR API] 認証エラー')
      throw new Error('認証が必要です。ログインしてください。')
    }
    if (error.code === 'deadline-exceeded') {
      console.error('[OCR API] タイムアウト')
      throw new Error('処理がタイムアウトしました。もう一度お試しください。')
    }
    if (error.message) {
      throw new Error(error.message)
    }

    console.error('[OCR API] 不明なエラー:', error)
    throw new Error('画像の解析に失敗しました。もう一度お試しください。')
  }
}
